
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import { useActivities } from "@/lib/use-api"

export function EngagementChart() {
  const { data: activities } = useActivities()

  // Build engagement data from real activities grouped by month
  const engagementData = (() => {
    if (!activities || activities.length === 0) {
      // Fallback so chart isn't empty on first load
      return [
        { month: "Jan", emails: 0, views: 0, meetings: 0 },
        { month: "Feb", emails: 0, views: 0, meetings: 0 },
      ]
    }
    const months: Record<string, { emails: number; views: number; meetings: number }> = {}
    for (const act of activities) {
      const date = new Date(act.created_at)
      const month = date.toLocaleString("default", { month: "short" })
      if (!months[month]) months[month] = { emails: 0, views: 0, meetings: 0 }
      if (act.type === "email") months[month].emails++
      else if (act.type === "view") months[month].views++
      else if (act.type === "meeting") months[month].meetings++
    }
    return Object.entries(months).map(([month, data]) => ({ month, ...data }))
  })()

  return (
    <div className="glass-strong rounded-xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-foreground">Engagement Trends</h3>
      <p className="text-xs text-muted-foreground mt-1">Email opens, deal room views, and meetings over time</p>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={engagementData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="emailGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ed4c4c" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#ed4c4c" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="viewGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#faa09a" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#faa09a" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="meetingGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(5, 30%, 92%)" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(0, 0%, 40%)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "hsl(0, 0%, 40%)" }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(8px)",
                border: "1px solid hsl(5, 30%, 90%)",
                borderRadius: "8px",
                fontSize: "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="views"
              stroke="#faa09a"
              strokeWidth={2}
              fill="url(#viewGrad)"
              name="Deal Room Views"
            />
            <Area
              type="monotone"
              dataKey="emails"
              stroke="#ed4c4c"
              strokeWidth={2}
              fill="url(#emailGrad)"
              name="Emails"
            />
            <Area
              type="monotone"
              dataKey="meetings"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#meetingGrad)"
              name="Meetings"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
